// Globals
import { Trans, useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getOrder, getOrders, selectAppliedFilters, selectOrder, setOrder } from '../../feature';

// Components
import OrdersTable from '../../components/OrdersTable/OrdersTable';
import AppraiserDetailsContainer from '../AppriserDetailsContainer/AppraiserDetailsContainer';
import DocumentsContainer from '../DocumentsContainer/DocumentsContainer';
import PhotoshootingContainer from '../PhotoshootingContainer/PhotoshootingContainer';
import SettlementDetailsContainer from '../SettlementDetailsContainer/SettlementDetailsContainer';
import OrdersFiltersContainer from '../OrdersFiltersContainer/OrdersFiltersContainer';
import {
  StyledContentBlock,
  StyledDrawer,
  StyledHeaderBlock,
  StyledPageTitle,
  StyledPageWrapper,
} from './OrdersContainerStyles';

// Models
import { OrderType } from '@/models/order.model';

// Helpers
import { GetOrdersDTO } from '../../dtos';
import { useGetOrders } from '../../hooks';
import AdContainer from '@/modules/Order/containers/AdContainer/AdContainer';

const getOrderDetails = (step_name?: OrderType) => {
  switch (step_name) {
    case OrderType.APPRAISER:
      return <AppraiserDetailsContainer />;
    case OrderType.PHOTOSHOOTING:
      return <PhotoshootingContainer />;
    case OrderType.DOCUMENTS:
      return <DocumentsContainer />;
    case OrderType.AD:
      return <AdContainer />;
    case OrderType.SETTLEMENT:
      return <SettlementDetailsContainer />;
    case OrderType.SERVICE_VIEWING_PACKAGE:
      return <SettlementDetailsContainer />;
    case OrderType.VIEW:
    case OrderType.BIDDING:
    case OrderType.CONTRACT:
    default:
      return null;
  }
};

const OrdersContainer = () => {
  const { t } = useTranslation('order');

  const { isLoading, orders, pagination } = useGetOrders();
  const appliedFilters = useAppState(selectAppliedFilters);
  const dispatch = useAppDispatch();
  const order = useAppState(selectOrder);

  const handleChangePage = (value: { cursor?: string; limit?: number }) => {
    const dto = new GetOrdersDTO({
      ...appliedFilters,
      ...value,
    });

    dispatch(getOrders(dto));
  };

  const handleSelectOrder = (id: string) => dispatch(getOrder(id));

  const handleClose = () => dispatch(setOrder(null));

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('orders_title')}</StyledPageTitle>
      </StyledHeaderBlock>
      <StyledContentBlock>
        <OrdersTable
          isLoading={isLoading}
          data={orders ?? []}
          onPageChange={handleChangePage}
          onRowClick={handleSelectOrder}
          pagination={pagination ?? {}}
          header={OrdersFiltersContainer}
        />
      </StyledContentBlock>
      <StyledDrawer
        title={
          <Trans
            ns="order"
            i18nKey="ordered_step"
            values={{ step: t(order?.step_name ?? ''), id: order?.tripletex_order_number }}
            components={{ span: <span /> }}
          />
        }
        width="68rem"
        open={!!order}
        onClose={handleClose}
      >
        {getOrderDetails(order?.step_name)}
      </StyledDrawer>
    </StyledPageWrapper>
  );
};

export default OrdersContainer;
